import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore, api } from '../lib/auth';
import { useTranslation } from '../lib/i18n';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import { toast } from 'sonner';
import { format, addMonths, subMonths, startOfMonth, endOfMonth, eachDayOfInterval, getDay, isSameDay, isWeekend, isBefore, startOfDay } from 'date-fns';
import { tr, enUS, nl } from 'date-fns/locale';
import { 
  ArrowLeft, 
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon, 
  Loader2,
  AlertCircle,
  Check,
  X,
  FileText,
  Send
} from 'lucide-react';
import { cn } from '../lib/utils';

const getDateLocale = (lang) => {
  switch (lang) {
    case 'tr': return tr;
    case 'nl': return nl;
    default: return enUS;
  }
};

const DEFAULT_COLORS = {
  ANNUAL_LEAVE: '#10B981',
  PATERNITY_LEAVE: '#3B82F6',
  KLEIN_VERLET: '#8B5CF6',
  UNPAID_LEAVE: '#F59E0B',
  SICK_LEAVE: '#EF4444',
};

export const CreateLeaveRequest = () => {
  const { t, language } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const locale = getDateLocale(language);
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // Wizard step
  const [step, setStep] = useState(1); // 1: Select Type, 2: Select Days & Description
  
  // Data
  const [balances, setBalances] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [blockedDays, setBlockedDays] = useState([]);
  const [lockedDates, setLockedDates] = useState([]);
  const [pendingDays, setPendingDays] = useState([]);
  
  // Form state
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedType, setSelectedType] = useState(null);
  const [selectedDays, setSelectedDays] = useState([]);
  const [description, setDescription] = useState('');
  
  // Calendar state
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [excludeWeekends] = useState(false);

  useEffect(() => {
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const balanceRes = await api.get(`/me/balances?year=${selectedYear}`);
      if (balanceRes.data.success) {
        setBalances(balanceRes.data.data.balances || []);
      }
      
      const typesRes = await api.get('/leave-types/active');
      if (typesRes.data.success) {
        setLeaveTypes(typesRes.data.data || []);
      }
      
      const startRange = `${selectedYear}-01-01`;
      const endRange = `${selectedYear}-12-31`;
      const blockedRes = await api.get(`/me/leave-calendar/blocked-days?start=${startRange}&end=${endRange}`);
      if (blockedRes.data.success) {
        setBlockedDays(blockedRes.data.data.blocked_days || []);
      }
      
      try {
        const lockedRes = await api.get(`/admin/locked-dates/for-user?start_date=${startRange}&end_date=${endRange}`);
        if (lockedRes.data.success) {
          setLockedDates(lockedRes.data.data || []);
        }
      } catch (err) {
        console.log('Could not fetch locked dates:', err);
      }
      
      try {
        const pendingRes = await api.get(`/leave/requests?year=${selectedYear}&status=PENDING`);
        if (pendingRes.data.success) {
          const pending = [];
          (pendingRes.data.data.requests || []).forEach(req => {
            const start = new Date(req.start_date);
            const end = new Date(req.end_date);
            const days = eachDayOfInterval({ start, end });
            days.forEach(d => pending.push(format(d, 'yyyy-MM-dd')));
          });
          setPendingDays(pending);
        }
      } catch (err) {
        console.log('Could not fetch pending requests:', err);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const selectedLeaveType = useMemo(() => {
    if (!selectedType) return null;
    return leaveTypes.find(lt => lt.code === selectedType);
  }, [selectedType, leaveTypes]);

  const selectedBalance = useMemo(() => {
    if (!selectedType) return null;
    return balances.find(b => b.leave_type_code === selectedType);
  }, [selectedType, balances]);

  const hideNumbers = user?.role === 'EMPLOYEE';
  const NO_RIGHT_MSG = 'Bu izin türü için yeterli izin hakkınız yok';

  const remainingDays = useMemo(() => {
    if (!selectedBalance) return 0;
    return selectedBalance.remaining_days || 0;
  }, [selectedBalance]);

  const getLeaveTypeColor = (code) => {
    const lt = leaveTypes.find(t => t.code === code);
    return lt?.color || DEFAULT_COLORS[code] || '#6B7280';
  };

  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
    
    const startDay = getDay(monthStart);
    const paddingDays = startDay === 0 ? 6 : startDay - 1;
    
    const paddedDays = [];
    for (let i = paddingDays; i > 0; i--) {
      const d = new Date(monthStart);
      d.setDate(d.getDate() - i);
      paddedDays.push({ date: d, isCurrentMonth: false });
    }
    
    days.forEach(d => paddedDays.push({ date: d, isCurrentMonth: true }));
    
    const endDay = getDay(monthEnd);
    const endPadding = endDay === 0 ? 0 : 7 - endDay;
    for (let i = 1; i <= endPadding; i++) {
      const d = new Date(monthEnd);
      d.setDate(d.getDate() + i);
      paddedDays.push({ date: d, isCurrentMonth: false });
    }
    
    return paddedDays;
  }, [currentMonth]);

  const isBlocked = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return blockedDays.includes(dateStr);
  };

  const isLocked = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return lockedDates.some(ld => ld.date === dateStr);
  };

  const getLockedReason = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const locked = lockedDates.find(ld => ld.date === dateStr);
    return locked?.reason ;
  };

  const isPending = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return pendingDays.includes(dateStr);
  };

  const isSunday = (date) => {
    return date.getDay() === 0;
  };

  const isSelectable = (date) => {
    const today = startOfDay(new Date());
    const dateYear = date.getFullYear();
    
    if (dateYear !== selectedYear) return false;
    if (isBefore(date, today)) return false;
    if (isSunday(date)) return false;
    if (isBlocked(date) || isPending(date) || isLocked(date)) return false;
    if (excludeWeekends && isWeekend(date)) return false;
    
    return true;
  };

  const getDayTooltip = (date) => {
    if (isLocked(date)) return getLockedReason(date);
    if (isSunday(date)) return 'Pazar günü kapalı';
    if (isBlocked(date)) return t('leave.approved') ;
    if (isPending(date)) return t('leave.pending') ;
    return null;
  };

  const isSelected = (date) => {
    return selectedDays.some(d => isSameDay(new Date(d), date));
  };

  const toggleDay = async (date) => {
    if (!isSelectable(date)) return;
    
    const dateStr = format(date, 'yyyy-MM-dd');
    
    if (isSelected(date)) {
      setSelectedDays(selectedDays.filter(d => d !== dateStr));
      return;
    }
    if (hideNumbers) {
      try {
        const res = await api.get('/me/balances/check', {
          params: { leave_type_code: selectedType, year: selectedYear, days: selectedDays.length + 1 },
        });
        if (!res.data?.data?.allowed) {
          toast.error(NO_RIGHT_MSG);
          return;
        }
      } catch {
        toast.error(t('common.error'));
        return;
      }
    } else if (selectedDays.length >= remainingDays) {
      toast.error(t('leave.insufficientBalance') );
      return;
    }
    setSelectedDays((prev) => (prev.includes(dateStr) ? prev : [...prev, dateStr].sort()));
  };

  const goToPrevMonth = () => setCurrentMonth(subMonths(currentMonth, 1));
  const goToNextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));

  const getWeekDays = () => {
    switch (language) {
      case 'tr': return ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];
      case 'nl': return ['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo'];
      default: return ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    }
  };

  const clearSelection = () => {
    setSelectedDays([]);
  };

  const handleSubmit = async () => {
    if (!selectedType) {
      toast.error(t('leave.selectTypeFirst') );
      return;
    }
    
    if (selectedDays.length === 0) {
      toast.error(t('leave.selectDays') );
      return;
    }

    setSubmitting(true);
    try {
      const sortedDays = [...selectedDays].sort();
      const startDate = sortedDays[0];
      const endDate = sortedDays[sortedDays.length - 1];
      
      const response = await api.post('/leave/requests', {
        leave_type_code: selectedType,
        year: selectedYear,
        start_date: startDate,
        end_date: endDate,
        selected_days: selectedDays,
        description: description || null
      });

      if (response.data.success) {
        toast.success(t('leave.requestCreated') );
        navigate('/dashboard/leave-requests');
      }
    } catch (error) {
      const detail = error.response?.data?.detail || t('common.error');
      // Handle overlapping dates error with specific dates
      if (detail.startsWith('OVERLAP_DATES:')) {
        const dates = detail.replace('OVERLAP_DATES:', '').trim();
        if (dates) {
          // Format dates nicely
          const formattedDates = dates.split(', ').map(d => {
            try {
              return format(new Date(d), 'd MMM', { locale });
            } catch {
              return d;
            }
          }).join(', ');
          toast.error(`${t('leave.datesOverlap')} (${formattedDates})`);
        } else {
          toast.error(t('leave.datesOverlap'));
        }
      } else if (detail.includes('overlapping dates')) {
        toast.error(t('leave.datesOverlap'));
      } else {
        toast.error(detail);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const goToNextStep = () => {
    if (!selectedType) {
      toast.error(t('leave.selectTypeFirst') );
      return;
    }
    setStep(2);
  };

  const goToPrevStep = () => {
    setStep(1);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  const selectedColor = selectedType ? getLeaveTypeColor(selectedType) : '#3B82F6';
  const selectedTypeName = selectedLeaveType?.name_i18n?.[language] || selectedLeaveType?.name_i18n?.tr || selectedType;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-800 lg:bg-transparent animate-fade-in" data-testid="create-leave-request">
      {/* Mobile Header */}
      <div className="sticky top-0 z-10 bg-white dark:bg-slate-800 border-b lg:relative lg:border-0 lg:bg-transparent">
        <div className="flex items-center justify-between p-4 lg:px-0">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="icon"
              className="h-9 w-9"
              onClick={() => step === 1 ? navigate('/dashboard/leave-requests') : goToPrevStep()}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-lg lg:text-2xl font-bold text-slate-900 dark:text-slate-100" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {t('leave.createRequest') }
              </h1>
              <p className="text-xs lg:text-sm text-slate-500 dark:text-slate-400">
                {step === 1 
                  ? (t('leave.step1Title') || 'Adım 1: İzin türü seçin') 
                  : (t('leave.step2Title') || 'Adım 2: Tarih seçin')
                }
              </p>
            </div>
          </div>
          
          {/* Year selector - only on step 1 */}
          {step === 1 && (
            <Select value={String(selectedYear)} onValueChange={(v) => {
              setSelectedYear(Number(v));
              setSelectedDays([]);
            }}>
              <SelectTrigger className="w-[100px] h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Array.from({ length: 3 }, (_, i) => new Date().getFullYear() + i).map(year => (
                  <SelectItem key={year} value={String(year)}>{year}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Progress indicator */}
        <div className="flex gap-2 px-4 pb-3 lg:hidden">
          <div className={cn("h-1 flex-1 rounded-full transition-all", step >= 1 ? "bg-blue-500" : "bg-slate-200")} />
          <div className={cn("h-1 flex-1 rounded-full transition-all", step >= 2 ? "bg-blue-500" : "bg-slate-200")} />
        </div>
      </div>

      {/* STEP 1: Select Leave Type */}
      {step === 1 && (
        <div className="p-4 lg:p-0 lg:max-w-4xl lg:mx-auto">
          {/* Selected type preview - if already selected */}
          {selectedType && (
            <div 
              className="mb-4 p-4 rounded-xl text-white flex items-center justify-between"
              style={{ backgroundColor: selectedColor }}
            >
              <div>
                <p className="text-white/80 text-sm">{t('leave.selectedType') }</p>
                <p className="text-xl font-bold">{selectedTypeName}</p>
              </div>
              {!hideNumbers && (
                <div className="text-right">
                  <p className="text-white/80 text-sm">{t('leave.remaining') }</p>
                  <p className="text-2xl font-bold">{remainingDays} <span className="text-base font-normal">{t('leave.days') }</span></p>
                </div>
              )}
            </div>
          )}

          {/* Leave Type Cards */}
          <div className="space-y-3">
            <Label className="text-sm font-medium text-slate-700 dark:text-slate-200">
              {t('leave.selectType') }
            </Label>
            
            {balances
              .filter((balance) => {
                const lt = leaveTypes.find(t => t.code === balance.leave_type_code);
                return lt && lt.is_active !== false;
              })
              .map((balance) => {
                const lt = leaveTypes.find(t => t.code === balance.leave_type_code);
                const color = lt?.color || DEFAULT_COLORS[balance.leave_type_code] || '#6B7280';
                const name = lt?.name_i18n?.[language] || lt?.name_i18n?.tr || balance.leave_type_code;
                const isSelectedType = selectedType === balance.leave_type_code;
                const noRight = hideNumbers && !balance.has_balance;
                
                return (
                  <button
                    key={balance.leave_type_code}
                    onClick={() => {
                      if (noRight) { toast.error(NO_RIGHT_MSG); return; }
                      setSelectedType(balance.leave_type_code);
                      setSelectedDays([]);
                    }}
                    aria-disabled={noRight}
                    data-testid={`leave-type-option-${balance.leave_type_code}`}
                    className={cn(
                      "w-full p-4 rounded-xl border-2 transition-all text-left",
                      "active:scale-[0.98]",
                      isSelectedType 
                        ? "border-slate-900 bg-white dark:bg-slate-800 shadow-lg" 
                        : "border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 hover:border-slate-300 hover:shadow",
                      noRight && "opacity-50 cursor-not-allowed"
                    )}
                  >
                    <div className="flex items-center gap-4">
                      <div 
                        className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: `${color}20` }}
                      >
                        <CalendarIcon className="h-6 w-6" style={{ color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-slate-900 dark:text-slate-100">{name}</div>
                        {hideNumbers ? (
                          noRight && (
                            <div className="text-sm text-slate-500 dark:text-slate-400 mt-0.5" data-testid={`leave-type-no-right-${balance.leave_type_code}`}>
                              İzin hakkınız bulunmuyor
                            </div>
                          )
                        ) : (
                          <div className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                            <span className="font-medium" style={{ color }}>{balance.remaining_days}</span>
                            <span className="text-slate-400"> / {balance.entitled_days} {t('leave.days') }</span>
                          </div>
                        )}
                      </div>
                      <div className={cn(
                        "w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all",
                        isSelectedType ? "border-slate-900 bg-slate-900" : "border-slate-300"
                      )}>
                        {isSelectedType && <Check className="h-4 w-4 text-white" />}
                      </div>
                    </div>
                  </button>
                );
              })}
          </div>

          {/* Next Button - Fixed at bottom on mobile */}
          <div className="fixed bottom-0 left-0 right-0 p-4 bg-white dark:bg-slate-800 border-t lg:relative lg:bg-transparent lg:border-0 lg:mt-6 lg:p-0">
            <Button
              className="w-full h-12 text-base font-semibold"
              onClick={goToNextStep}
              disabled={!selectedType}
              style={{ backgroundColor: selectedType ? selectedColor : undefined }}
            >
              {t('common.next') }
              <ArrowRight className="h-5 w-5 ml-2" />
            </Button>
          </div>
          
          {/* Spacer for fixed button on mobile */}
          <div className="h-20 lg:hidden" />
        </div>
      )}

      {/* STEP 2: Select Days & Description */}
      {step === 2 && (
        <div className="lg:max-w-4xl lg:mx-auto">
          {/* Selected Type Header - Mobile */}
          <div 
            className="p-4 text-white lg:rounded-xl lg:mx-0 lg:mb-4"
            style={{ backgroundColor: selectedColor }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/80 text-sm">{selectedTypeName}</p>
                <p className="text-xl font-bold" data-testid="leave-selected-days-count">
                  {hideNumbers ? selectedDays.length : `${selectedDays.length} / ${remainingDays}`} <span className="text-base font-normal">{t('leave.days') }</span>
                </p>
              </div>
              {selectedDays.length > 0 && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={clearSelection}
                  className="text-white hover:bg-white dark:bg-slate-800/20"
                >
                  <X className="h-4 w-4 mr-1" />
                  {t('common.clear') }
                </Button>
              )}
            </div>
          </div>

          {/* Calendar */}
          <div className="bg-white dark:bg-slate-800 lg:rounded-xl lg:border">
            {/* Calendar Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <Button variant="ghost" size="icon" onClick={goToPrevMonth}>
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <h3 className="font-semibold text-lg">
                {format(currentMonth, 'MMMM yyyy', { locale })}
              </h3>
              <Button variant="ghost" size="icon" onClick={goToNextMonth}>
                <ChevronRight className="h-5 w-5" />
              </Button>
            </div>

            {/* Week Headers */}
            <div className="grid grid-cols-7 border-b">
              {getWeekDays().map((day, i) => (
                <div 
                  key={i} 
                  className={cn(
                    "py-2 text-center text-xs font-medium",
                    i >= 5 ? "text-slate-400" : "text-slate-600 dark:text-slate-300"
                  )}
                >
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7">
              {calendarDays.map(({ date, isCurrentMonth }, idx) => {
                const isToday = isSameDay(date, new Date());
                const selected = isSelected(date);
                const blocked = isBlocked(date);
                const pending = isPending(date);
                const locked = isLocked(date);
                const sunday = isSunday(date);
                const selectable = isSelectable(date);
                const tooltip = getDayTooltip(date);
                
                return (
                  <button
                    key={idx}
                    onClick={() => toggleDay(date)}
                    disabled={!selectable}
                    title={tooltip || undefined}
                    className={cn(
                      "relative aspect-square flex items-center justify-center transition-all",
                      "border-b border-r border-slate-100 dark:border-slate-700",
                      "active:scale-95",
                      !isCurrentMonth && "bg-slate-50 dark:bg-slate-800",
                      sunday && "bg-slate-200 cursor-not-allowed",
                      locked && "bg-red-100 cursor-not-allowed",
                      selectable && "hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-800 cursor-pointer",
                      !selectable && !locked && !sunday && "cursor-not-allowed opacity-40",
                    )}
                    style={{
                      backgroundColor: selected ? `${selectedColor}20` : undefined,
                    }}
                  >
                    <span className={cn(
                      "text-sm font-medium w-9 h-9 flex items-center justify-center rounded-full transition-all",
                      isToday && !selected && "ring-2 ring-slate-900",
                      !isCurrentMonth && "text-slate-300",
                      sunday && isCurrentMonth && "text-slate-400",
                      isCurrentMonth && !isToday && !sunday && "text-slate-700 dark:text-slate-200",
                      locked && "text-red-600 line-through",
                      blocked && isCurrentMonth && "bg-emerald-500 text-white",
                      pending && isCurrentMonth && "bg-amber-500 text-white",
                      selected && "text-white",
                    )}
                    style={{
                      backgroundColor: selected ? selectedColor : undefined,
                    }}
                    >
                      {format(date, 'd')}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Legend */}
            <div className="flex flex-wrap gap-3 p-4 border-t text-xs">
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded" style={{ backgroundColor: selectedColor }} />
                <span className="text-slate-600 dark:text-slate-300">{t('leave.selected') }</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded bg-emerald-500" />
                <span className="text-slate-600 dark:text-slate-300">{t('leave.approved') }</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded bg-amber-500" />
                <span className="text-slate-600 dark:text-slate-300">{t('leave.pending') }</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded bg-red-400" />
                <span className="text-slate-600 dark:text-slate-300">{t('leave.locked') }</span>
              </div>
            </div>
          </div>

          {/* Selected Days Preview */}
          {selectedDays.length > 0 && (
            <div className="p-4 bg-white dark:bg-slate-800 border-t lg:rounded-xl lg:border lg:mt-4">
              <Label className="text-sm font-medium text-slate-700 dark:text-slate-200 mb-2 block">
                {t('leave.selectedDays') } ({selectedDays.length})
              </Label>
              <div className="flex flex-wrap gap-2">
                {selectedDays.map(day => (
                  <Badge 
                    key={day} 
                    className="text-white cursor-pointer hover:opacity-80 px-3 py-1"
                    style={{ backgroundColor: selectedColor }}
                    onClick={() => setSelectedDays(selectedDays.filter(d => d !== day))}
                  >
                    {format(new Date(day), 'd MMM', { locale })}
                    <X className="h-3 w-3 ml-1" />
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Description */}
          <div className="p-4 bg-white dark:bg-slate-800 border-t lg:rounded-xl lg:border lg:mt-4">
            <Label className="text-sm font-medium text-slate-700 dark:text-slate-200 mb-2 flex items-center gap-2">
              <FileText className="h-4 w-4 text-slate-400" />
              {t('leave.description') } 
              <span className="text-slate-400 font-normal">({t('common.optional') })</span>
            </Label>
            <Textarea
              placeholder={t('leave.descriptionPlaceholder') || 'İzin talebiniz hakkında not ekleyin...'}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="resize-none"
            />
          </div>

          {/* Submit Button - Fixed at bottom on mobile */}
          <div className="fixed bottom-0 left-0 right-0 p-4 bg-white dark:bg-slate-800 border-t lg:relative lg:bg-transparent lg:border-0 lg:mt-6 lg:p-0 lg:pb-8">
            {selectedDays.length === 0 && (
              <p className="text-center text-sm text-slate-500 dark:text-slate-400 mb-3 lg:hidden">
                {t('leave.selectDaysHint') }
              </p>
            )}
            <Button
              className="w-full h-12 text-base font-semibold"
              onClick={handleSubmit}
              disabled={submitting || selectedDays.length === 0}
              style={{ backgroundColor: selectedColor }}
            >
              {submitting ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <>
                  <Send className="h-5 w-5 mr-2" />
                  {t('leave.submitRequest') }
                </>
              )}
            </Button>
          </div>
          
          {/* Spacer for fixed button on mobile */}
          <div className="h-24 lg:hidden" />
        </div>
      )}
    </div>
  );
};

export default CreateLeaveRequest;
